'use client';
/* Built In Imports */
import { useState } from 'react';

/* External Imports */
import { Box, Image, Flex } from '@chakra-ui/react';
import { nanoid } from 'nanoid';
import Slider from 'react-slick';

/* Internal Imports */
/* Components */
// import { consoleLog } from '@components/Utility/Shared/SharedService';
import config from '@config';

/* Services */
/* Styles */
import SliderDots from '@app-ila/components/Icons/SliderDots';

import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';

/**
 * Render Slider Image Gallery Component
 *
 * @param sectionContent.sectionContent
 * @param {object} sectionContent
 * @returns {ReactElement} Slider Image Gallery Component
 */
const Gallery = ({ sectionContent }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const images = sectionContent?.images?.map(image => image.url);

  const NextArrow = ({ onClick }) => {
    return (
      <Box
        as="div"
        pos="absolute"
        right={{md:"13%", lg:"6%"}}
        top={{md:"45%", lg:"47%"}}
        transform="translateY(-50%)"
        zIndex="10"
        display={{ base: 'none', md: 'block' }}
      >
        <Box
          cursor="pointer"
          bgColor="#fff"
          border="0"
          borderRadius="50%"
          w={{ base: '32px', sm: '32px', md: '54px', lg: '54px' }}
          h={{ base: '32px', sm: '32px', md: '56px', lg: '56px' }}
          bgPos="center"
          bgRepeat="no-repeat"
          color="transparent"
          bgImage={`url(${config.staticPath}/assets/images/right-arrow.svg)`}
          bgSize={{ base: '7px', sm: '7px', md: '10px', lg: '10px' }}
          boxShadow="rgba(0, 0, 0, 0.3) 0 0 15px 0"
          onClick={onClick}
        />
      </Box>
    );
  };

  const PrevArrow = ({ onClick }) => {
    return (
      <Box
        as="div"
        pos="absolute"
        left={{md:"13%", lg:"6%"}}
        top={{md:"45%", lg:"47%"}}    
        transform="translateY(-50%)"
        zIndex="10"
        display={{ base: 'none', md: 'block' }}
      >
        <Box
          cursor="pointer"
          bgColor="#fff"
          border="0"
          borderRadius="50%"
          w={{ base: '32px', sm: '32px', md: '54px', lg: '54px' }}
          h={{ base: '32px', sm: '32px', md: '56px', lg: '56px' }}
          bgPos="center"
          bgRepeat="no-repeat"
          color="transparent"
          bgImage={`url(${config.staticPath}/assets/images/left-arrow.svg)`}
          bgSize={{ base: '7px', sm: '7px', md: '10px', lg: '10px' }}
          boxShadow="rgba(0, 0, 0, 0.3) 0 0 15px 0"
          onClick={onClick}
        />
      </Box>
    );
  };


  const slider_settings = {
    dots: true,
    infinite: true,
    centerMode: true,
    autoplay: true,
    lazyLoad: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    centerPadding: '0',
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    beforeChange: (prev, current) => setCurrentSlide(current),
    responsive: [
      {
        breakpoint: 1170,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 834,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1,
          initialSlide: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
    ],
    appendDots: dots => (
      <Flex height="40px" position="relative">
        <Flex
          marginRight="auto"
          marginLeft="auto"
          width="178px"
          mt="20px"
          justifyContent="space-between"
        >
          {dots.map(dot => {
            const button = dot.props.children;

            return (
              <button
                key={dot.key}
                style={{
                  display: 'flex', // Ensure the button content aligns properly
                  justifyContent: 'center', // Center the content horizontally
                  alignItems: 'center', // Center the content vertically
                  width: '16px',
                  height: '16px',
                  border: 'none', // Remove default browser border styles
                  padding: '0', // Ensure no extra padding
                  margin: '0', // Avoid unwanted margins
                  borderRadius: '50%',
                  boxSizing: 'border-box', // Ensure padding and borders are included in dimensions
                  backgroundColor:
                  currentSlide.toString() === dot.key ? '#000641' : '#DDDDDD',
                  cursor: 'pointer',
                }}
                onClick={e => {
                  button.props.onClick(e);
                  setCurrentSlide(dot.key);
                }}
              >
                <SliderDots
                  color={currentSlide.toString() === dot.key ? '#000641' : '#DDDDDD'}
                  style={{
                    width: '100%',
                    height: '100%',
                  }} 
                />
              </button>
            );
          })}
        </Flex>
      </Flex>
    ),
    customPaging: i => (
      <div
        style={{
          marginTop: '40px',
          width: '8px',
          height: '8px',
          border: 'solid #DDDDDD',
          borderRadius: '50%',
          cursor: 'pointer',
          backgroundColor: '#DDDDDD',
        }}
      ></div>
    ),
  };

  return (
    <Box mb={{ base: '30px', md: '50px' }} mt={{ base: '40px', md: '40px' }}>
      <Box p="0px" id="slider-common" w="100%" className="image-gallery">
        <Flex position="relative" id='z-indexed-color' zIndex={5} display={{ base: 'none', xl: 'flex' }}>
          <Box
            position="absolute"
            top="64px"
            left="0"
            height="437px"
            width="208px"
            bgColor="transparent"
            background="linear-gradient(to right, #081B5C, rgba(0, 0, 255, 0))"
          />
          <Box
            position="absolute"
            top="64px"
            right="0"
            height="437px"
            width="208px"
            bgColor="transparent"
            background="linear-gradient(to left, #081B5C, rgba(0, 0, 255, 0))"
          />
        </Flex>
        <Slider {...slider_settings}>
          {sectionContent?.images?.map((item, index) => (
            <Box key={nanoid()} outline="none" textAlign="center" mx="auto">
              <Box
                m={{ base: '5px', md: '0', lg: '0' }}
                transform={{ base: 'translateX(-97px)', lg:'translateX(-128px)', xl: 'translateX(-162px)'}}
                height={{base:"270px", md:"365px", xl:"565px"}}
                maxW="365px"
                overflow="hidden"
                // cursor="pointer"
                transition="all 0.5s linear"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Image
                  width={{ base: '130px', md: '181px', lg: '230px', xl: '287px' }}
                  height={{ base: '200px', md: '275px', xl: '437px' }}
                  transition="all 0.5s ease"
                  src={item.url}
                  alt={item.alt}
                  title={item.title}
                  objectFit="cover"
                />
              </Box>
            </Box>
          ))}
        </Slider>
        <style global jsx>{`
          /* Style for inactive dots */
          #slider-common .slick-dots li button {
            border-radius: 50%;
            width: 10px;
            height: 10px;
            transition: all 0.3s ease;
          }

          /* Style for the active dot */
          #slider-common .slick-slide.slick-center img {
            width: 363px;
            height: 550px;
          }
          @media (max-width: 1024px) {
            #slider-common .slick-slide.slick-center img {
              width: 228px;
              height: 347px;
            }
          }
          @media (max-width: 480px) {
            #slider-common .slick-slide.slick-center img {
              width: 180px;
              height: 260px;
            }
          }
        `}</style>
      </Box>
    </Box>
  );
};

export default Gallery;